import type { EntitlementMap, ProductEntitlement } from '../types/entitlements';
import { runtimeEnv } from '../runtimeEnv';

// Org-level terms acceptance (EIT-140). `accepted` is this org's state; `url` is the hosted
// terms the Hub links to. Both come from the /me response's terms block.
export type TermsInfo = {
  accepted: boolean;
  url: string;
  // ISO 8601; non-null only when terms are accepted.
  acceptedAt: string | null;
};

export type MeResponse = {
  products: EntitlementMap;
  // Always populated by mapMeResponse (fail-closed default); optional so unrelated fixtures/callers
  // that only care about products need not construct it.
  terms?: TermsInfo;
};

type WireGrant = { basis: string; expiresAt?: string | null };
type WireProduct = {
  status: 'active' | 'available' | 'hidden';
  trialAvailable?: boolean;
  roleBasedAccess?: boolean;
  grants?: WireGrant[];
  features?: string[];
};
export type WireMeResponse = {
  products: Partial<Record<string, WireProduct>>;
  terms?: { accepted?: boolean; url?: string; acceptedAt?: string | null };
};

function latestExpiresAt(grants: WireGrant[]): string | undefined {
  return grants
    .map((g) => g.expiresAt)
    .filter((iso): iso is string => !!iso)
    .filter((iso) => !Number.isNaN(new Date(iso).getTime()))
    .reduce<string | undefined>(
      (latest, iso) => (!latest || new Date(iso).getTime() > new Date(latest).getTime() ? iso : latest),
      undefined,
    );
}

// Lapsed grants are retained by the backend (they mark a trial as taken), so only live
// grants may drive what the user sees — a stale trial must not outrank a live subscription.
function isLive(grant: WireGrant): boolean {
  return !grant.expiresAt || new Date(grant.expiresAt).getTime() > Date.now();
}

function mapProduct(wire: WireProduct): ProductEntitlement {
  const mapped: ProductEntitlement = { status: wire.status };
  // Set before the non-active return: the flag matters most on products the org doesn't have.
  if (wire.trialAvailable) mapped.trialAvailable = true;
  if (wire.roleBasedAccess) mapped.roleBasedAccess = true;
  if (wire.status !== 'active') return mapped;

  const live = (wire.grants ?? []).filter(isLive);
  const trial = live.find((g) => g.basis === 'trial');
  if (trial) {
    mapped.plan = 'trial';
    if (trial.expiresAt) mapped.trialEndsAt = trial.expiresAt;
  } else {
    const renewalDate = latestExpiresAt(live);
    if (renewalDate) mapped.renewalDate = renewalDate;
  }
  return mapped;
}

export function mapMeResponse(raw: WireMeResponse): MeResponse {
  const products: EntitlementMap = {};
  for (const [key, wire] of Object.entries(raw.products)) {
    // 'hidden' means "catalogue-known, no self-serve offer yet" — before the server listed
    // every catalogue product these were simply absent, and absence is what every surface
    // renders as a cross-sell product. Passing 'hidden' through made those tiles vanish.
    if (!wire || wire.status === 'hidden') continue;
    products[key] = mapProduct(wire);
  }
  // Fail-closed: a missing or malformed terms block reads as NOT accepted, never accepted.
  const accepted = raw.terms?.accepted === true;
  const acceptedAt = accepted && typeof raw.terms?.acceptedAt === 'string' ? raw.terms.acceptedAt : null;
  const terms: TermsInfo = { accepted, url: raw.terms?.url ?? '', acceptedAt };
  return { products, terms };
}

export class MeFetchError extends Error {
  constructor(public readonly status: number, public readonly body: string, url: string) {
    super(`${url} failed: ${status}`);
    this.name = 'MeFetchError';
  }
}

// A /me that never answers would otherwise leave every caller on its loader for good; past this
// the request is abandoned and the caller shows its error state instead.
export const ME_TIMEOUT_MS = 10_000;

export class MeTimeoutError extends Error {
  constructor(url: string) {
    super(`${url} timed out`);
    this.name = 'MeTimeoutError';
  }
}

export async function fetchMe(token: string, signal?: AbortSignal): Promise<MeResponse> {
  const url = `${runtimeEnv.entitlementsApiUrl}/me`;
  // The caller's signal still aborts as an AbortError (an unmount, nothing to show); only the
  // timeout surfaces as MeTimeoutError, which callers treat like any other failure.
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => { timedOut = true; controller.abort(); }, ME_TIMEOUT_MS);
  const forward = () => controller.abort();
  if (signal?.aborted) controller.abort();
  else signal?.addEventListener('abort', forward, { once: true });
  try {
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    });
    if (!res.ok) {
      const body = await res.text();
      throw new MeFetchError(res.status, body, url);
    }
    return mapMeResponse(await res.json());
  } catch (e) {
    if (timedOut) throw new MeTimeoutError(url);
    throw e;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', forward);
  }
}
